import { Component, Input, EventEmitter, Output, HostListener, HostBinding } from '@angular/core';
import type { OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ProjectDto, ClientDto, UserRoleDto } from "../../data/CommandServiceDtos.generated";
import { ProjectCardMetricsDto } from './project-card-metrics.model';
import {
  faPencilAlt, faWrench, faDollyFlatbed, faClipboardList, faLink, faStar,
  faEllipsisV, faUser, faBuilding, faCheckCircle, faExclamationTriangle, faClock, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { PageContainerService } from '../../services';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

type MetricTone = 'good' | 'ahead' | 'watch' | 'late' | 'neutral';

interface CardMetric {
  key: string;
  label: string;
  value: number | null;
  display: string;
  tone: MetricTone;
}

@Component({
  standalone: false,
  selector: 'project-thumbnail',
  templateUrl: './project-thumbnail.component.html',
  styleUrls: ['./project-thumbnail.component.scss']
})
export class ProjectThumbnailComponent implements OnInit, OnChanges {
  @Input() project: ProjectDto;
  @Input() userRoles: UserRoleDto[];
  @Input() isFavoriteEnabled: boolean = true;
  /** 'grid' = portfolio card, 'favorite' = highlighted favorite card */
  @Input() layout: 'grid' | 'favorite' = 'grid';
  @Input() cardMetrics: ProjectCardMetricsDto;
  @Input() metricsLoading = false;
  @Output() favoriteToggled = new EventEmitter<ProjectDto>();

  @HostBinding('class.layout-favorite')
  get isFavoriteLayout(): boolean {
    return this.layout === 'favorite';
  }

  showEditIcon: boolean = false;
  showEditIconForUser: boolean = false;
  menuOpen = false;
  faPencil = faPencilAlt;
  faSpinner = faSpinner;
  faWorkPackIcon = faWrench;
  faMaterialLink = faDollyFlatbed;
  iconExecutionLink = faClipboardList;
  faCXPlannerLink = faLink;
  faStar = faStar;
  faEllipsisV = faEllipsisV;
  faUser = faUser;
  faBuilding = faBuilding;
  faCheckCircle = faCheckCircle;
  faExclamationTriangle = faExclamationTriangle;
  faClock = faClock;
  cxPlannerSummaryStage: any;
  favorites: ProjectDto[] = [];

  metrics: CardMetric[] = [
    { key: 'ready', label: 'READY', value: null, display: 'N/A', tone: 'neutral' },
    { key: 'matl', label: "MAT'L", value: null, display: 'N/A', tone: 'neutral' },
    { key: 'exec', label: 'EXEC', value: null, display: 'N/A', tone: 'neutral' },
    { key: 'comm', label: 'COMM', value: null, display: 'N/A', tone: 'neutral' }
  ];
  statusLabel = 'On track';
  statusTone: MetricTone = 'neutral';
  statusIcon = faClock;

  constructor(private pcs: PageContainerService, private http: HttpClient, private route: Router) {
  }

  ngOnInit() {
    this.checkUserRoles();
    this.loadFavorites();
    this.statusLabel = this.project?.Active ? 'Active' : 'Inactive';
    this.statusTone = this.project?.Active ? 'good' : 'neutral';
    this.statusIcon = this.project?.Active ? faCheckCircle : faClock;
    this.applyCardMetrics(this.cardMetrics);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['project'] && this.project) {
      this.statusLabel = this.project.Active ? 'Active' : 'Inactive';
      this.statusTone = this.project.Active ? 'good' : 'neutral';
      this.statusIcon = this.project.Active ? faCheckCircle : faClock;
    }
    if (changes['cardMetrics'] || changes['project'] || changes['metricsLoading']) {
      this.applyCardMetrics(this.cardMetrics);
    }
  }

  get assetLabel(): string {
    return (this.project?.Asset?.Name || '').toUpperCase();
  }

  get showMetricSkeleton(): boolean {
    return this.metricsLoading && !this.cardMetrics;
  }

  /**
   * The server served a previous value while it recomputes. The numbers shown are real, just not
   * confirmed current, so the card says so rather than presenting them as up to date.
   */
  get metricsAreStale(): boolean {
    return !!(this.cardMetrics && this.cardMetrics.IsStale);
  }

  get metricsAsOfLabel(): string {
    if (!this.metricsAreStale || !this.cardMetrics.AsOf) {
      return '';
    }
    const asOf = new Date(this.cardMetrics.AsOf);
    return isNaN(asOf.getTime()) ? 'Updating…' : `Updating… showing ${asOf.toLocaleTimeString()}`;
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.menuOpen = false;
  }

  truncate(value: string, max: number): string {
    if (!value) {
      return '';
    }
    return value.length > max ? value.slice(0, max) + '...' : value;
  }

  loadFavorites() {
    const favoriteIds = JSON.parse(localStorage.getItem('favoriteProjects') || '[]');
    if (favoriteIds.includes(this.project.ProjectId) && !this.favorites.find(f => f.ProjectId === this.project.ProjectId)) {
      this.favorites.push(this.project);
    }
  }

  isProjectFavorited(projectId: number): boolean {
    try {
      const favorites = JSON.parse(localStorage.getItem('favoriteProjects') || '[]');
      if (!Array.isArray(favorites)) {
        return false;
      }
      const id = Number(projectId);
      return favorites.some((stored: any) => Number(stored) === id);
    } catch {
      return false;
    }
  }

  toggleFavorite(event: MouseEvent, project: ProjectDto): void {
    event.preventDefault();
    event.stopPropagation();

    let favoritesIds = JSON.parse(localStorage.getItem('favoriteProjects') || '[]');
    if (!Array.isArray(favoritesIds)) {
      favoritesIds = [];
    }
    const projectId = Number(project.ProjectId);

    if (favoritesIds.some((id: any) => Number(id) === projectId)) {
      favoritesIds = favoritesIds.filter((id: any) => Number(id) !== projectId);
      this.favorites = this.favorites.filter(f => Number(f.ProjectId) !== projectId);
    } else {
      favoritesIds.push(projectId);
      this.favorites.push(project);
    }

    localStorage.setItem('favoriteProjects', JSON.stringify(favoritesIds));

    this.favoriteToggled.emit(project);
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('favoritesUpdated', { detail: { projectId: project.ProjectId } }));
    }
  }

  openProject() {
    this.route.navigate(['/project', this.project.ProjectId]);
  }

  openReadiness(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen = false;

    if (this.project.IsReadinessFormEnabled || this.project.DisplayReadiness) {
      this.route.navigate(['/readiness-import', this.project.ProjectId]);
      return;
    }

    if (this.project.ReadinessAssessmentPath) {
      window.open(this.project.ReadinessAssessmentPath, '_blank');
    }
  }

  async openMaterials(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen = false;

    try {
      const response = await this.pcs.apiService.GetMaterialsStatus(
        { ProjectId: this.project.ProjectId }, false, false);
      if (response?.Materials?.MaterialsAccessUrl) {
        window.open(response.Materials.MaterialsAccessUrl, '_blank');
        return;
      }
    } catch {
      // Fall through to project page
    }

    this.route.navigate(['/project', this.project.ProjectId]);
  }

  async openExecution(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen = false;

    try {
      const response = await this.pcs.apiService.GetExecutionStatus(
        { ProjectId: this.project.ProjectId }, false, false);
      if (response?.Execution?.ExecutionAccessUrl) {
        window.open(response.Execution.ExecutionAccessUrl.replace(/^http:\/\//i, 'https://'), '_blank');
        return;
      }
    } catch {
      // Fall through to project page
    }

    this.route.navigate(['/project', this.project.ProjectId]);
  }

  gotoCXPlannerDashboard(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.cxPlannerSummaryStage?.projectId) {
      window.open("https://app.cxplanner.com/project/" + this.cxPlannerSummaryStage.projectId + "/dashboard", "_blank");
    }
  }

  toggleMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  editProject(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen = false;
    this.route.navigate(['/edit-project', this.project.ProjectId]);
  }

  async openInProPak(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen = false;

    try {
      const response = await this.pcs.apiService.GetReadinessSummary(
        { ProjectId: this.project.ProjectId, ProPakProjectId: this.project.ProjectId },
        false,
        false
      );
      const summary = response?.ReadinessSummary;
      const userName = this.pcs.clientSessionService.userName;
      const token = summary?.PassThroughAuthKey;
      const baseUrl = summary?.ProPakProjectUrl;
      if (baseUrl && userName && token) {
        window.open(this.buildProPakPassThroughUrl(baseUrl, userName, token, this.project.ProjectId), '_blank');
        return;
      }
    } catch {
      // handled below
    }

    this.pcs.notificationService.warning(
      'ProPak',
      'Unable to open this project in ProPak right now.',
      { timeOut: 3000 }
    );
  }

  toggleEdit() {
    this.showEditIcon = !this.showEditIcon;
  }

  clientLogo(client: ClientDto) {
    if (!client || !client.hasOwnProperty('logo')) {
      return 'app/assets/images/project-placeholder.png';
    }
    return '';
  }

  checkUserRoles() {
    this.showEditIconForUser = this.userRoles && this.userRoles.some(role =>
      role.RoleName === 'Global Administrator' ||
      role.RoleName === 'Company Administrator'
    );
  }

  hasAnyProjectIcons(project: any): boolean {
    return !!(
      project && (
        project.ReadinessAssessmentPath ||
        (project.SiteSenseIdentifiers && project.SiteSenseIdentifiers.length > 0) ||
        (project.PrimaveraIdentifiers && project.PrimaveraIdentifiers.length > 0) ||
        (project.CXPlannerIdentifiers && project.CXPlannerIdentifiers.length > 0)
      )
    );
  }

  private buildProPakPassThroughUrl(
    baseUrl: string,
    userName: string,
    authKey: string,
    projectId: number
  ): string {
    const base = (baseUrl || '').split('#')[0];
    return `${base}#${userName},${authKey},${projectId}`;
  }

  private resetMetrics() {
    this.metrics.forEach(metric => {
      metric.value = null;
      metric.display = 'N/A';
      metric.tone = 'neutral';
    });
    this.statusLabel = this.project?.Active ? 'Active' : 'Inactive';
    this.statusTone = this.project?.Active ? 'good' : 'neutral';
    this.statusIcon = this.project?.Active ? faCheckCircle : faClock;
  }

  private applyCardMetrics(metrics: ProjectCardMetricsDto) {
    this.resetMetrics();
    if (!metrics) {
      return;
    }
    if (metrics.ReadyPercent != null) {
      this.setMetric('ready', Number(metrics.ReadyPercent));
    }
    if (metrics.MaterialsPercent != null) {
      this.setMetric('matl', Number(metrics.MaterialsPercent));
    }
    if (metrics.ExecutionPercent != null) {
      this.setMetric('exec', Number(metrics.ExecutionPercent));
    }
    if (metrics.CommPercent != null) {
      this.setMetric('comm', Number(metrics.CommPercent));
    }
    this.applyScheduleStatus(metrics.BaselineFinishDate, metrics.ProjectFinishDate);
  }

  private setMetric(key: string, value: number) {
    const metric = this.metrics.find(m => m.key === key);
    if (!metric) {
      return;
    }
    const n = Math.max(0, Math.min(100, Number(value)));
    const rounded = Math.round(n * 10) / 10;
    metric.value = rounded;
    metric.display = Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
    if (rounded >= 80) {
      metric.tone = 'good';
    } else if (rounded >= 30) {
      metric.tone = 'watch';
    } else if (rounded > 0) {
      metric.tone = 'late';
    } else {
      metric.tone = 'neutral';
    }
  }

  private applyScheduleStatus(baselineFinish?: string, projectFinish?: string) {
    if (!baselineFinish || !projectFinish) {
      return;
    }
    const baseline = new Date(baselineFinish);
    const finish = new Date(projectFinish);
    if (isNaN(baseline.getTime()) || isNaN(finish.getTime())) {
      return;
    }
    const diffDays = Math.round((finish.getTime() - baseline.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 7) {
      this.statusLabel = `Proj. finish +${diffDays}d late`;
      this.statusTone = 'late';
      this.statusIcon = faClock;
    } else if (diffDays > 0) {
      this.statusLabel = `Watch +${diffDays}d slip`;
      this.statusTone = 'watch';
      this.statusIcon = faExclamationTriangle;
    } else if (diffDays < 0) {
      this.statusLabel = `${Math.abs(diffDays)}d ahead`;
      this.statusTone = 'ahead';
      this.statusIcon = faCheckCircle;
    } else {
      this.statusLabel = 'On baseline finish';
      this.statusTone = 'good';
      this.statusIcon = faCheckCircle;
    }
  }

  private averageCxPercent(stages: any): number | null {
    if (!Array.isArray(stages) || stages.length === 0) {
      return null;
    }
    const values = stages.map((s: any) => Number(s.PercentComplete)).filter((n: number) => !isNaN(n));
    if (!values.length) {
      return null;
    }
    return values.reduce((a: number, b: number) => a + b, 0) / values.length;
  }

  private transformData(dataArray: any[]): any[] {
    return dataArray.map((data) => {
      return {
        SummaryStageId: data.id,
        StageName: data.name,
        PercentComplete: data.sub_per,
        Stages: data.children ? data.children.map((child: any): any => ({
          StageId: child.id,
          StageName: child.name,
          PercentComplete: child.sub_per
        })) : []
      };
    });
  }
}
